import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface ClientConfig {
  client: {
    name: string;
    subtitle: string;
    logo: string;
    favicon: string;
  };
  branding: {
    primaryColor: string;
    primaryHover: string;
    primaryDark: string;
    primaryLight: string;
    accentColor: string;
    accentLight: string;
    backgroundColor: string;
    cardBackground: string;
    textPrimary: string;
    textMuted: string;
    borderColor: string;
  };
  kiosk: {
    exitPassword: string;
    inactivityTimeoutSeconds: number;
    showBestScores: boolean;
  };
  games: {
    memory: { enabled: boolean; pairs: number };
    puzzle: { enabled: boolean };
    reaction: { enabled: boolean };
    simon: { enabled: boolean; colors: number; speedMs: number };
    whack: { enabled: boolean; durationSeconds: number };
    speedtap: { enabled: boolean; durationSeconds: number };
    balloonpop: { enabled: boolean; durationSeconds: number };
  };
  records: {
    enabled: boolean;
    requireRut: boolean;
    requireName: boolean;
    maxEntriesPerGame: number;
  };
}

const DEFAULT_CONFIG: ClientConfig = {
  client: {
    name: 'Entel',
    subtitle: 'Zona de entretenimiento',
    logo: 'assets/entel-logo.png',
    favicon: 'favicon.ico',
  },
  branding: {
    primaryColor: '#1400FF',
    primaryHover: '#0f00cc',
    primaryDark: '#0a0099',
    primaryLight: '#6b5cff',
    accentColor: '#FF6600',
    accentLight: '#ff8533',
    backgroundColor: '#f0f4f8',
    cardBackground: '#ffffff',
    textPrimary: '#1e293b',
    textMuted: '#636e72',
    borderColor: '#e2e8f0',
  },
  kiosk: {
    exitPassword: '1234',
    inactivityTimeoutSeconds: 120,
    showBestScores: true,
  },
  games: {
    memory: { enabled: true, pairs: 8 },
    puzzle: { enabled: true },
    reaction: { enabled: true },
    simon: { enabled: true, colors: 9, speedMs: 400 },
    whack: { enabled: true, durationSeconds: 30 },
    speedtap: { enabled: true, durationSeconds: 30 },
    balloonpop: { enabled: true, durationSeconds: 45 },
  },
  records: {
    enabled: true,
    requireRut: true,
    requireName: true,
    maxEntriesPerGame: 10,
  },
};

@Injectable({
  providedIn: 'root',
})
export class ClientConfigService {
  private config$ = new BehaviorSubject<ClientConfig>(DEFAULT_CONFIG);

  constructor(private http: HttpClient) {}

  loadConfig(): Observable<ClientConfig> {
    return this.http.get<ClientConfig>('assets/client-config.json').pipe(
      tap((config) => {
        this.config$.next(config);
        this.applyBranding(config.branding);
      }),
      catchError(() => {
        console.warn('No se pudo cargar client-config.json, usando configuración por defecto');
        this.applyBranding(DEFAULT_CONFIG.branding);
        return of(DEFAULT_CONFIG);
      })
    );
  }

  getConfig(): ClientConfig {
    return this.config$.value;
  }

  getConfig$(): Observable<ClientConfig> {
    return this.config$.asObservable();
  }

  applyRemoteConfig(apiData: any): ClientConfig {
    const config: ClientConfig = {
      client: apiData.client || {},
      branding: apiData.branding || {},
      kiosk: apiData.kiosk || {},
      games: apiData.games || {},
      records: apiData.records || {},
    };
    this.config$.next(config);
    this.applyBranding(config.branding);
    return config;
  }

  private applyBranding(branding: ClientConfig['branding']): void {
    const root = document.documentElement;
    root.style.setProperty('--kiosk-primary', branding.primaryColor);
    root.style.setProperty('--kiosk-primary-hover', branding.primaryHover);
    root.style.setProperty('--kiosk-primary-dark', branding.primaryDark);
    root.style.setProperty('--kiosk-primary-light', branding.primaryLight);
    root.style.setProperty('--kiosk-accent', branding.accentColor);
    root.style.setProperty('--kiosk-accent-light', branding.accentLight);
    root.style.setProperty('--kiosk-bg', branding.backgroundColor);
    root.style.setProperty('--kiosk-card-bg', branding.cardBackground);
    root.style.setProperty('--kiosk-text', branding.textPrimary);
    root.style.setProperty('--kiosk-text-muted', branding.textMuted);
    root.style.setProperty('--kiosk-border', branding.borderColor);
  }
}
